import { Checkbox } from '@orbind/ui'
import { useMemo, useState } from 'react'
import { useT } from '../../lib/i18n.tsx'
import { useSession } from '../../lib/session.tsx'
import { useWorkItemList } from './api.ts'
import { StatusPill } from './bits.tsx'
import { passaNaSelecao, useFiltroDeItens } from './filtro-de-itens.ts'
import { FiltroDaLista, type WiSelecao } from './QuickFilterBar.tsx'

/**
 * "Selecionar item existente" — UM seletor para o checklist, o item filho e os
 * itens relacionados (SPEC-20261008-2157-filtro-no-seletor-de-itens, pedido do
 * Cristian: "deve possuir filtro para selecionar somente itens avulsos, todos
 * ou poder adicionar filtros como o filtro de itens no topo do kanban e item
 * de trabalho, pode reaproveitar").
 *
 * - "Todos" / "Só avulsos": avulso é o item SEM PAI (o mesmo conceito da
 *   lista de Itens);
 * - o filtro da lista de Itens, igual: Todos / Meus / Atrasados, os filtros
 *   rápidos salvos e as condições por campo — inclusive criar um novo ali;
 * - a busca casa código e título, sem acento e sem caixa;
 * - marca vários e confirma de uma vez (ou um só, quando `umSo`).
 */

const LIMITE = 200
const semAcento = (texto: string) => texto.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()

export function SeletorDeItens({
  titulo,
  excluir,
  umSo = false,
  rotuloConfirmar,
  onConfirmar,
  onFechar,
}: {
  titulo: string
  /** Ids que não podem ser escolhidos (o próprio item, os já incluídos…). */
  excluir: ReadonlySet<string>
  umSo?: boolean
  /** Rótulo do botão; recebe quantos estão marcados. */
  rotuloConfirmar: (quantos: number) => string
  /** Inclui os marcados; devolve os que NÃO entraram (vazio = fecha). */
  onConfirmar: (ids: string[]) => Promise<string[]>
  onFechar: () => void
}) {
  const t = useT()
  const eu = useSession()
  const lista = useWorkItemList({ todos: true })
  const todosOsItens = lista.data?.items ?? []
  const etapas = useMemo(
    () => todosOsItens.flatMap((i) => (i.stage?.step ? [{ fluxo: i.stage.flow ?? null, etapa: i.stage.step }] : [])),
    [todosOsItens],
  )
  const filtro = useFiltroDeItens(etapas)

  const [busca, setBusca] = useState('')
  const [soAvulsos, setSoAvulsos] = useState(false)
  const [selecao, setSelecao] = useState<WiSelecao>({ tipo: 'builtin', id: 'all' })
  const [marcados, setMarcados] = useState<string[]>([])
  const [incluindo, setIncluindo] = useState(false)

  const termo = semAcento(busca.trim())
  const achados = todosOsItens.filter(
    (row) =>
      !excluir.has(row.id) &&
      (!soAvulsos || !row.parentWorkItemId) &&
      (termo === '' || semAcento(`${row.code} ${row.title}`).includes(termo)) &&
      passaNaSelecao(row, selecao, eu.id, filtro.rowValues),
  )

  const statusLabel = (code: string) => {
    const key = `work_item_status.${code.toLowerCase()}.name`
    const label = t(key)
    return label === key ? code : label
  }

  const confirmar = async () => {
    if (marcados.length === 0 || incluindo) return
    setIncluindo(true)
    try {
      const restantes = await onConfirmar(marcados)
      if (restantes.length === 0) onFechar()
      else setMarcados(restantes)
    } finally {
      setIncluindo(false)
    }
  }

  return (
    <>
      <div className="wi-modal__backdrop" onClick={incluindo ? undefined : onFechar} />
      <div className="wi-modal wi-modal--pick wi-modal--pick-largo" role="dialog" aria-label="Selecionar item existente">
        <div style={{ flex: 'none', padding: '16px 18px 10px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 className="wi-modal__title" style={{ margin: 0, flex: 1 }}>
              {titulo}
            </h2>
            <button type="button" className="wi-panel__close" style={{ position: 'static', transform: 'none' }} aria-label="Fechar" onClick={onFechar}>
              <i className="fas fa-times" aria-hidden style={{ fontSize: 12 }} />
            </button>
          </div>
          <div style={{ position: 'relative' }}>
            <i className="fas fa-search" aria-hidden style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--ob-text-muted)' }} />
            <input
              className="wi-panel__search"
              style={{ height: 38, paddingLeft: 34, marginBottom: 0 }}
              autoFocus
              placeholder="Buscar por código ou título…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          {/* filtros: avulsos e o filtro da lista de Itens */}
          <div className="wi-pick__filtros">
            <div className="wi-pick__seg" role="radiogroup" aria-label="Quais itens">
              <button type="button" role="radio" aria-checked={!soAvulsos} className={!soAvulsos ? 'is-on' : ''} onClick={() => setSoAvulsos(false)}>
                Todos
              </button>
              <button type="button" role="radio" aria-checked={soAvulsos} className={soAvulsos ? 'is-on' : ''} onClick={() => setSoAvulsos(true)}>
                <i className="fas fa-circle-notch" aria-hidden /> Só avulsos
              </button>
            </div>
            <FiltroDaLista
              selecao={selecao}
              onSelecionar={setSelecao}
              statusOptions={filtro.statusOptions}
              typeOptions={filtro.typeOptions}
              priorityOptions={filtro.priorityOptions}
              memberOptions={filtro.memberOptions}
              loadValueOptions={filtro.loadValueOptions}
              campos={filtro.campos}
            />
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 140, overflowY: 'auto', padding: '4px 10px 12px' }}>
          {lista.isPending ? <p className="wi-pick__vazio">Carregando os itens…</p> : null}
          {achados.slice(0, LIMITE).map((row) => (
            <Checkbox
              key={row.id}
              className="wi-menu__opt wi-pick__opt"
              checked={marcados.includes(row.id)}
              disabled={incluindo}
              onChange={() =>
                setMarcados((atual) =>
                  umSo
                    ? atual.includes(row.id)
                      ? []
                      : [row.id]
                    : atual.includes(row.id)
                      ? atual.filter((id) => id !== row.id)
                      : [...atual, row.id],
                )
              }
              label={
                <>
                  <span className="wi-cl__wicode">{row.code}</span>
                  <span className="wi-pick__title">{row.title}</span>
                  <StatusPill tone={row.status.tone}>{statusLabel(row.status.code)}</StatusPill>
                </>
              }
            />
          ))}
          {lista.data && achados.length === 0 ? <p className="wi-pick__vazio">Nenhum item com esse filtro.</p> : null}
          {achados.length > LIMITE ? (
            <p className="wi-pick__vazio">
              Mostrando {LIMITE} de {achados.length} — refine a busca ou o filtro.
            </p>
          ) : null}
        </div>
        <div className="wi-pick__foot">
          <span className="wi-pick__count">
            {marcados.length === 0
              ? umSo
                ? 'Marque o item'
                : 'Marque os itens para incluir'
              : `${marcados.length} marcado(s)`}
          </span>
          <button type="button" className="ob-rte__cancel" onClick={onFechar} disabled={incluindo}>
            Cancelar
          </button>
          <button
            type="button"
            className="ob-rte__save"
            style={{ opacity: marcados.length > 0 && !incluindo ? 1 : 0.5 }}
            disabled={marcados.length === 0 || incluindo}
            onClick={() => void confirmar()}
          >
            {incluindo ? 'Incluindo…' : rotuloConfirmar(marcados.length)}
          </button>
        </div>
      </div>
    </>
  )
}
